/** DOM attribute the web app stamps test ids on; Playwright's `testIdAttribute` must match. */
export const TEST_ID_ATTRIBUTE = "data-sb";

/**
 * Registry of every browser-test selector id.
 *
 * Shared by `@storyboard/web` components (which stamp them via `data-sb={…}`) and `@storyboard/web-e2e` (which
 * selects on them). Adding a selector here is the only way to grow the harness's selectable surface, so renames and
 * deletions break at typecheck instead of at test runtime.
 *
 * Values are stable dot-namespaced strings; rows repeated per entity keep one static id and are disambiguated in
 * tests by content (e.g. driver name) rather than dynamic ids.
 */
export const TestIds = {
  dispatch: {
    /** The Dispatch grid's scroll container: the element a journey scrolls and reads the scroll position of. */
    table: "dispatch.table",
    /** The area the grid fills under the header; its height sets how many rows a page key moves. */
    tableArea: "dispatch.table-area",
    /** A row in the Dispatch grid; disambiguate by driver name or alias. */
    row: "dispatch.row",
    /** The "Make a task…" button on a row's detail and in the bulk actions. */
    task: "dispatch.task",
    /** The task picker: what a task is about, the board to pin it to, and its note. */
    taskDialog: "dispatch.task-dialog",
    /** The task picker's About header: the drivers' names, or "N drivers" for more than fit. */
    taskAbout: "dispatch.task-about",
    /** The task picker's board dropdown. */
    taskBoard: "dispatch.task-board",
    /** The task picker's note field. */
    taskNote: "dispatch.task-note",
    /** The task picker's "Clears after" dropdown: acknowledgement, a preset, a set time, or the expiry in force. */
    taskExpiry: "dispatch.task-expiry",
    /** The task picker's set-time date and time field, shown once "A set time…" is chosen. */
    taskExpiryAt: "dispatch.task-expiry-at",
    /** The task picker's Submit, or in edit mode Move or Save, which makes the task or pins it with its expiry. */
    taskSubmit: "dispatch.task-submit",
    /** The purple chip on a row with an open task, naming its board. */
    taskChip: "dispatch.task-chip",
    taskLine: "dispatch.task-line",
    /** The "N drivers" count in the Dispatch header, reflecting the active filters. */
    count: "dispatch.count",
    /** The Dispatch search field (the id sits on the field's root; the input is inside). */
    search: "dispatch.search",
    /** The "Filter" button in the Dispatch header; opens the popover holding the view's controls. */
    filterMenu: "dispatch.filter-menu",
    /** The open Filter popover's body. */
    filterPopover: "dispatch.filter-popover",
    /** A toggle filter control in the open Filter popover; disambiguate by its label. */
    filterToggle: "dispatch.filter-toggle",
    /**
     * A value-picking filter control in the open Filter popover; disambiguate by its label.
     *
     * A values control's picker opens the values editor; a time control's is a select of spans.
     */
    filterSelect: "dispatch.filter-select",
    /** The values editor a Filter popover picker opens over the popover: the column menu's checklist, Apply, Clear. */
    filterValuesEditor: "dispatch.filter-values-editor",
    /** One chip of an enum or boolean values control in the open Filter popover; disambiguate by its label. */
    filterChip: "dispatch.filter-chip",
    /** The always-present Requires Acknowledgement picker in the open Filter popover. */
    filterRequiresAcknowledgement: "dispatch.filter-requires-acknowledgement",
    /** One chosen entry of the Requires Acknowledgement picker, with its remove button; disambiguate by its label. */
    filterRequiresAcknowledgementChip: "dispatch.filter-requires-acknowledgement-chip",
    /** The view picker beside the page title; only rendered when the config cascade yields more than one view. */
    viewSwitcher: "dispatch.view-switcher",
    /** The team (channel) picker beside the view name; absent unless the workspace keys more than one team. */
    teamDropdown: "dispatch.team-dropdown",
    /** A team choice in the open team picker; disambiguate by its label. */
    teamOption: "dispatch.team-option",
    /** The detail block under the selected row: the inline reply and the row's actions. */
    rowDetail: "dispatch.row-detail",
    /** The inline reply composer in the selected row's detail (the id sits on its root; the textarea is inside). */
    composer: "dispatch.composer",
    /** The composer's send button; disabled until the draft has text or a file and a driver is selected. */
    composerSend: "dispatch.composer.send",
    /** The composer's attach button (row and thread composers alike); opens the upload popover. */
    composerAttach: "dispatch.composer.attach",
    /** The upload popover the attach button opens, holding the drop zone. */
    composerAttachPopover: "dispatch.composer.attach-popover",
    /** The chip naming the uploaded file waiting in the composer for Send. */
    composerAttachment: "dispatch.composer.attachment",
    /** The composer's templates button (row and thread composers alike); opens the template list. */
    composerTemplates: "dispatch.composer.templates",
    /** The popover composer's record button, one of its tools beside Attach and Templates; the row's reply has none. */
    composerRecord: "dispatch.composer.record",
    /** The overlay covering the composer while a voice message records, processes, or uploads. */
    composerRecording: "dispatch.composer.recording",
    /** The template list the templates button opens. */
    composerTemplatesPopover: "dispatch.composer.templates-popover",
    /** One template in the open list; disambiguate by its name. */
    composerTemplate: "dispatch.composer.template",
    /** The search box at the top of the open template list; narrows it by name or text. */
    composerTemplateSearch: "dispatch.composer.template-search",
    /** The keyboard button in the Dispatch header; folds the shortcut bar, and the full list drops from it. */
    shortcuts: "dispatch.shortcuts",
    /** The full list of shortcuts, one per row in the bar's order; absent from the DOM while closed. */
    shortcutsList: "dispatch.shortcuts-list",
    /** The All shortcuts entry pinned at the shortcut bar's right edge; opens the full list. */
    allShortcuts: "dispatch.all-shortcuts",
    /** The shortcut bar under the header, listing every bound shortcut in the helper's order. */
    keyboardLegend: "dispatch.keyboard-legend",
    /** One shortcut in the bar or the full list: its keycaps and its label; disambiguate by the label. */
    shortcutEntry: "dispatch.shortcut-entry",
    /** A shortcut's label inside its entry, apart from the keycaps, so a journey can read the list's order. */
    shortcutLabel: "dispatch.shortcut-label",
    /** The muted strip under the filter bar listing every condition narrowing the rows. */
    queryStrip: "dispatch.query-strip",
    /** One chip in the query strip; disambiguate by its text. */
    queryChip: "dispatch.query-chip",
    /** The "Clear all" action in the query strip. */
    queryClearAll: "dispatch.query-clear-all",
    /** The row context menu offering "Only …" and "Exclude …" conditions per visible field. */
    rowMenu: "dispatch.row-menu",
    /** The "Group by" picker in the open Display popover; opens a searched list of every field, column or not. */
    groupBy: "dispatch.group-by",
    /** The "Sort by" picker in the open Display popover; opens a searched list of every field, column or not. */
    sortBy: "dispatch.sort-by",
    /** A group header row in the Dispatch grid; disambiguate by its label. A click folds or unfolds the group. */
    group: "dispatch.group",
    /** The right-hand History/Context popover (the id sits on the drawer paper). */
    popover: "dispatch.popover",
    /** The popover's header row: Back, the driver's name and unit number, the Call button, and the two tabs. */
    popoverHeader: "dispatch.popover.header",
    /** The unit number beside the driver's name in the popover header; absent when the driver runs no truck. */
    popoverUnit: "dispatch.popover.unit",
    /** The Call button in the popover header; absent when the driver has no dialable number. */
    popoverCall: "dispatch.popover.call",
    /** A tab in the popover; disambiguate by its label ("History" / "Context"). */
    popoverTab: "dispatch.popover.tab",
    /** The Context tab: the Info, HOS, Route, and Status sections, top to bottom. */
    contextPanel: "dispatch.context-panel",
    /** The Context tab's Info section: Driver beside Truck Assignment, then PTA beside Location. */
    contextInfo: "dispatch.context-info",
    /** The Info section's Driver field: the driver's name, with their username under it when they have one. */
    contextDriver: "dispatch.context-driver",
    /** The Info section's Truck Assignment field: the unit the driver runs, and the team pill when a team runs it. */
    contextTruckAssignment: "dispatch.context-truck-assignment",
    /** The Info section's PTA field: when the route frees the driver, and the HOS rests the forecast projects first. */
    contextPta: "dispatch.context-pta",
    /** The PTA field's Factors, closed by default: the projected HOS rests; absent when HOS was not walked. */
    contextPtaFactors: "dispatch.context-pta-factors",
    /** The Info section's Location field: the driver's nearest city and state, with a Google Maps link. */
    contextLocation: "dispatch.context-location",
    /** The Location field's source-and-age line under the country: "Platform Science · 2 min ago". */
    contextLocationProvenance: "dispatch.context-location-provenance",
    /** The Context tab's HOS section: the duty status beside every clock's time left, over the recap. */
    contextHos: "dispatch.context-hos",
    /** The HOS section's duty status cell: the status with its icon, and when it changed. */
    contextHosDutyStatus: "dispatch.context-hos-duty-status",
    /** One clock's cell in the HOS section; disambiguate by its label. */
    contextHosClock: "dispatch.context-hos-clock",
    /** The HOS section's recap line: the hours returning at the start of each of the next days. */
    contextHosRecap: "dispatch.context-hos-recap",
    /** The Context tab's Route section: the route's name and progress over every stop with its status. */
    contextRoute: "dispatch.context-route",
    /** The team pill in the Truck Assignment field, present only when two or more drivers run the route. */
    contextTeam: "dispatch.context-team",
    /** The route head's reference numbers under the route's name: BOL, PO, Trailer, and the rest, a label each. */
    contextRouteReferences: "dispatch.context-route-references",
    /** The route head's Progress, beside the route's name: the miles completed, the stops done, when it started. */
    contextRouteProgress: "dispatch.context-route-progress",
    /** The route head's miles line: miles completed over the route's total. */
    contextRouteMiles: "dispatch.context-route-miles",
    /** The Route section's strip between the notes and the stops: the route leg by leg, a marker per stop. */
    contextRouteStrip: "dispatch.context-route-strip",
    /** A stop's marker on the strip, in run order; `data-done` says whether it is passed, `data-role` its kind. */
    contextRouteStop: "dispatch.context-route-stop",
    /** The Route section's truck icon, on the strip: mid-leg while driving, on the stop's marker once there. */
    contextTruck: "dispatch.context-truck",
    /** The Route section's notes: what the dispatcher wrote on the order, a line each. */
    contextRouteNotes: "dispatch.context-route-notes",
    /** One stop's row in the Route section; disambiguate by the stop's name. */
    contextStop: "dispatch.context-stop",
    /** A stop row's handling word (Live, Drop, …), only when the vendor coded one; `data-handling` is the code. */
    contextStopHandling: "dispatch.context-stop-handling",
    /** A stop row's "City, ST" line, present only when the vendor stated both. */
    contextStopPlace: "dispatch.context-stop-place",
    /** A stop row's Map link, present only when the vendor stated the stop's coordinates. */
    contextStopMap: "dispatch.context-stop-map",
    /** The Next Stop cell's stop-type marker (S, C, F, T); disambiguate by `data-role`. */
    nextStopMarker: "dispatch.next-stop-marker",
    /** The Context tab's Status section: the tile grid of the driver's device and availability. */
    contextStatus: "dispatch.context-status",
    /** One tile in the Context tab's Status grid; disambiguate by its caption. */
    contextTile: "dispatch.context-tile",
    /** The "Display" button in the Dispatch header; opens the popover with grouping, sort, and the column lists. */
    displayMenu: "dispatch.display-menu",
    /** A column's row in the open Display popover; disambiguate by its label. */
    columnMenuRow: "dispatch.column-menu.row",
    /** The reset action in the Display popover; disabled while nothing differs from the view definition. */
    columnReset: "dispatch.column-reset",
    /** The popover a click on a column header opens: sort, a filter editor for the column's kind, pin, hide. */
    columnMenu: "dispatch.column-menu",
    /**
     * One value row in an open values editor, the column menu's or the Filter popover's; a click toggles it.
     *
     * Disambiguate by its label.
     */
    columnFilterValue: "dispatch.column-filter.value",
    /**
     * An open values editor's "Select all" row: ticks every value the search shows, or unticks them all.
     *
     * In the column menu or the Filter popover; a single-value control's editor has none.
     */
    columnFilterSelectAll: "dispatch.column-filter.select-all",
    /**
     * An open values editor's "(blank)" row: keeps the rows where the column has no value.
     *
     * Only the column menu's editor offers it, never the Filter popover's.
     */
    columnFilterBlank: "dispatch.column-filter.blank",
    /** A segmented choice in the open column menu's filter editor (a mode or a unit); disambiguate by its label. */
    columnFilterOption: "dispatch.column-filter.option",
    /**
     * An open filter editor's "Apply" action: sets the column's conditions, or the control, to what it holds.
     *
     * In the column menu or the Filter popover's values editor.
     */
    columnFilterApply: "dispatch.column-filter.apply",
    /**
     * An open filter editor's "Clear" action: drops the column's conditions, or empties the control.
     *
     * In the column menu or the Filter popover's values editor.
     */
    columnFilterClear: "dispatch.column-filter.clear",
    /** The red dot in a row's leading cell while the row waits on an acknowledgement. */
    rowDot: "dispatch.row.dot",
    /** The bar that replaces the count while rows are ticked: how many, and a clear action. */
    selectionBar: "dispatch.selection-bar",
    /** The header's "Acknowledge" action: acknowledges every ticked row; disabled while nothing is ticked. */
    acknowledgeSelected: "dispatch.selection.acknowledge",
    /** The header's "Fleet message" action: composes to the ticked drivers, else to every driver shown. */
    fleetMessage: "dispatch.fleet.open",
    /** The Dispatch fleet message composer. */
    fleetDialog: "dispatch.fleet.dialog",
    /** The composer's line saying who gets the message: names for a few, a count for many. */
    fleetRecipients: "dispatch.fleet.recipients",
    /** The composer's read-only chips repeating the page's filters when the recipients are every driver shown. */
    fleetFilters: "dispatch.fleet.filters",
    /** The fleet composer's Templates button; opens the template list. Disabled while sending or sending a file. */
    fleetTemplates: "dispatch.fleet.templates",
    /** The template list the fleet composer's Templates button opens; its items carry `composerTemplate`. */
    fleetTemplatesPopover: "dispatch.fleet.templates-popover",
    /** The Display popover's "Rows" select: a driver per row or a message per row, over the view's grain. */
    rowsKind: "dispatch.rows-kind",
    /** The History tab's thread: the driver's messages and the events between them, oldest first. */
    thread: "dispatch.thread",
    /** The date divider before a day's first entry in the thread; disambiguate by its `MMM d, yyyy` date. */
    threadDate: "dispatch.thread.date",
    /** One message bubble in the thread; disambiguate by its text. */
    threadMessage: "dispatch.thread.message",
    /** One event pill in the thread (an acknowledgement, say); disambiguate by its text. */
    threadEvent: "dispatch.thread.event",
    /** One task card in the thread, under the message or event it is about or on its own; disambiguate by its note. */
    threadTask: "dispatch.thread.task",
    /** The chip on a History task card naming the board the task stands on. */
    threadTaskBoard: "dispatch.thread.task-board",
    /** The menu a right-click in History opens: on a message or event, a task card, or the thread around them. */
    threadMenu: "dispatch.thread.menu",
    /** The "Listen" button on a thread message with audio; a second press pauses. */
    threadListen: "dispatch.thread.listen",
    /** The driver's listen receipt in a thread message's caption: "Heard", or "Played" when they may not have heard. */
    threadReceipt: "dispatch.thread.receipt",
    /** The activity button in a thread message's caption; opens who saw, heard, and acknowledged the message. */
    threadActivity: "dispatch.thread.activity",
    /** The open activity list for one thread message: one line per receipt, or that there is none yet. */
    threadActivityList: "dispatch.thread.activity-list",
    /** The popover's one composer, at its foot under either tab (the id sits on its root; the textarea is inside). */
    threadComposer: "dispatch.thread.composer",
    /** The popover composer's send button. */
    threadSend: "dispatch.thread.send",
  },
  driverView: {
    /** A row in the driver roster grid. */
    row: "driver-view.row",
    /** The red unread indicator inside a roster row; only rendered while the row has unreads. */
    rowUnreadDot: "driver-view.row.unread-dot",
    /** A roster tab button; disambiguate by its label ("My Drivers" / "All Drivers"). */
    tab: "driver-view.tab",
    /** The unread-count badge on the My Drivers tab; absent while subscribed drivers have no unreads. */
    tabUnreadBadge: "driver-view.tab.unread-badge",
    /** The roster's global search field (the id sits on the field's root; the input is inside). */
    search: "driver-view.search",
    /** Clear button inside the search field; only rendered while a query is present. */
    searchClear: "driver-view.search.clear",
    /** Toggle narrowing the roster to drivers with unread messages. */
    unreadFilterToggle: "driver-view.unread-filter-toggle",
    /** Row-count summary line ("N of M Drivers Visible"). */
    metaRow: "driver-view.meta-row",
    /** Context-menu action marking the targeted drivers' channels as read. */
    contextMenuMarkRead: "driver-view.context-menu.mark-read",
    /** A column's header; disambiguate by its title, and read in DOM order for the grid's column order. */
    columnHeader: "driver-view.column-header",
    /** The sort/filter dropdown trigger inside a column header. */
    columnFilter: "driver-view.column-filter",
    /** A filter value listed in an open column dropdown. */
    filterOption: "driver-view.filter-option",
    /** A sort choice listed in an open column dropdown. */
    sortOption: "driver-view.sort-option",
    /** Search field narrowing the values an open column dropdown lists. */
    filterSearch: "driver-view.filter-search",
    /** Action selecting every value an open column dropdown lists; only offered once a search has narrowed them. */
    filterSelectAll: "driver-view.filter-select-all",
    /** Action clearing the open column dropdown's filter. */
    filterClearAll: "driver-view.filter-clear-all",
    /** Toolbar action dropping every active filter at once; absent while none are active. */
    clearFilters: "driver-view.clear-filters",
    /** Toolbar trigger opening the column manager. */
    columnManager: "driver-view.column-manager",
    /** A column's row in the open column manager; disambiguate by its label. */
    columnManagerRow: "driver-view.column-manager.row",
    /** Drag handle on a column manager row; dragging it reorders the grid's columns. */
    columnManagerDragHandle: "driver-view.column-manager.drag-handle",
    /** Drag handle inside a column header; dragging it reorders the grid's columns. */
    columnHeaderGrabber: "driver-view.column-header.grabber",
    /** Resize handle on a column header's trailing edge; drag it to resize, double-click it to fit content. */
    columnResizeHandle: "driver-view.column-header.resize-handle",
    /** Pin toggle on a column manager row, moving that column to the front of the grid. */
    columnManagerPin: "driver-view.column-manager.pin",
    /** Action restoring the workspace's default columns. */
    columnManagerReset: "driver-view.column-manager.reset",
    /** One value of a multi-value tag, rendered as a chip in the driver's row. */
    rowTagChip: "driver-view.row.tag-chip",
    /** The muted glyph in a row's Device Volume cell; only rendered while the device reports itself inaudible. */
    rowVolumeMuted: "driver-view.row.volume-muted",
    /** The text of a row's Location cell, with its source and age as `aria-label`; grey once the location is stale. */
    rowLocation: "driver-view.row.location",
    /** The link in a row's Location cell opening Google Maps; rendered only when the location has no place name. */
    locationCoordinates: "driver-view.location-coordinates",
    /** A segmented choice in the location dropdown (Address / Coordinates / Miles / Kilometers). */
    locationOption: "driver-view.location-option",
    /** Latitude field in the location dropdown's coordinates mode (the input is inside). */
    locationLatitude: "driver-view.location-latitude",
    /** Longitude field in the location dropdown's coordinates mode (the input is inside). */
    locationLongitude: "driver-view.location-longitude",
    /** Applies the location dropdown's radius query to the roster. */
    locationApply: "driver-view.location-apply",
    /** Clears the location dropdown's radius query. */
    locationClear: "driver-view.location-clear",
    /** The keyboard-shortcuts pane. */
    keyboardShortcuts: "driver-view.keyboard-shortcuts",
    /** One binding's row in the keyboard-shortcuts pane, carrying its label and its keycaps. */
    keyboardShortcutRow: "driver-view.keyboard-shortcut-row",
    /** Toolbar dropdown naming the dispatcher's current team; absent unless the workspace defines several. */
    teamDropdown: "driver-view.team-dropdown",
    /** A team choice in the open team dropdown; disambiguate by its label. */
    teamOption: "driver-view.team-option",
  },
  feed: {
    /**
     * Fills the feed panel, so it is visible exactly while a channel is on screen.
     *
     * The panel collapses to zero width instead of unmounting, and the feed inside keeps intrinsic width, which
     * fools element-level visibility checks — this wrapper's box is the honest open/closed signal.
     */
    pane: "feed.pane",
    /** Scroll container of the opened feed's item list. */
    itemList: "feed.item-list",
    /** The "New" divider marking the first unread item in an open feed. */
    unreadDivider: "feed.unread-divider",
    /** One whole rendered message in an open feed: author line, content card, and receipts. */
    item: "feed.item",
    /** Rendered transcript content of a single feed item. */
    itemTranscript: "feed.item.transcript",
    /** One listen-receipt chip on a feed item, labeled with how the message reached the driver. */
    itemReceipt: "feed.item.receipt",
    /** The silent-message marker on a feed item posted without hands-free playback. */
    itemSilent: "feed.item.silent",
    /** A link attached to a feed item, rendered as an anchor carrying its URL. */
    itemLink: "feed.item.link",
    /** One TMS event chip between a driver's messages; disambiguate by its text. */
    tmsEventChip: "feed.tms-event-chip",
    /** The detail a TMS event chip or thread pill opens on click: a rejection's status above the driver's call. */
    tmsEventDetail: "feed.tms-event-detail",
    /** The open channel's message composer (the box the dispatcher types into). */
    composer: "feed.composer",
    /** Why the dispatcher can't post in the open feed; absent while the composer is available. */
    footerMessage: "feed.footer-message",
    /** Kebab-menu trigger on a feed item. */
    itemMenu: "feed.item.menu",
    /** The item-menu action starting a geo fleet message centered on the message's location. */
    itemMenuFleetMessage: "feed.item.menu.fleet-message",
  },
  fleet: {
    /** Toolbar action opening the fleet-message composer. */
    open: "fleet.open",
    /** The composer's message text box (the id sits on a wrapper; the textarea is inside). */
    messageInput: "fleet.message-input",
    /** The silent-send toggle beside the message label. */
    silent: "fleet.silent",
    /** The composer's send action. */
    send: "fleet.send",
    /** Chip counting the currently selected recipients. */
    recipientCount: "fleet.recipient-count",
    /** One recipient option in the composer's picker (selected drivers, All, My, from the table); pick by label. */
    recipientOption: "fleet.recipient-option",
    /** Returns to the driver table to change which rows are checked. */
    editSelection: "fleet.edit-selection",
    /** Primary action of the "choose drivers from the table" option; leaves the composer for the table. */
    goToDriverTable: "fleet.go-to-driver-table",
    /** Search field narrowing the recipient list by channel title (the input is inside). */
    recipientFilter: "fleet.recipient-filter",
    /** One selectable channel row in the recipient picker; disambiguate by title. */
    recipient: "fleet.recipient",
    /** A collapsible group header in the recipient picker; disambiguate by name. */
    recipientGroup: "fleet.recipient-group",
    /** Selects or clears every channel in the open group at once. */
    recipientGroupToggle: "fleet.recipient-group-toggle",
    /** The "All <team>" checkbox selecting every channel keyed to one command alias. */
    recipientAlias: "fleet.recipient-alias",
    /** The "My Drivers (N)" checkbox selecting every channel of the sender's own book. */
    recipientMyDrivers: "fleet.recipient-my-drivers",
    /** A composer tab (recipients, geo); disambiguate by label. */
    tab: "fleet.tab",
    /** Toggle showing the workspace's message templates. */
    templates: "fleet.templates",
    /** One template in the open template list; disambiguate by name. */
    template: "fleet.template",
    /** The post-send confirmation pane. */
    success: "fleet.success",
    /** Geo tab: latitude input (the input is inside). */
    geoLatitude: "fleet.geo.latitude",
    /** Geo tab: longitude input (the input is inside). */
    geoLongitude: "fleet.geo.longitude",
    /** Geo tab: radius input (the input is inside). */
    geoRadius: "fleet.geo.radius",
    /** Geo tab: a distance-unit choice (Miles / Kilometers); disambiguate by label. */
    geoUnit: "fleet.geo.unit",
    /** Geo tab: header row counting the drivers the radius query matched; toggles them all. */
    geoMatches: "fleet.geo.matches",
    /** Geo tab: one driver the radius query matched; disambiguate by title. */
    geoMatch: "fleet.geo.match",
    /** Geo tab: notice that the message the composer was opened from has no location. */
    geoNoLocation: "fleet.geo.no-location",
  },
  readOnly: {
    /** Sidebar toggle putting the dispatcher into read-only mode; absent unless the workspace allows it. */
    toggle: "read-only.toggle",
    /** Confirmation that leaving read-only mode may mark the open channel read. */
    confirmDisable: "read-only.confirm-disable",
  },
  nav: {
    /** The icon-rail navigation column; pages are opened via role+name inside it. */
    sidebar: "nav.sidebar",
  },
  modal: {
    /** The close control in a modal's header. */
    close: "modal.close",
  },
  search: {
    /** The global search modal's query field (the input is inside). */
    input: "search.input",
    /** Clears the query; only rendered while one is present. */
    clear: "search.clear",
    /** Runs the query; rendered beside the empty-state prompt and the stale-results hint. */
    submit: "search.submit",
    /** One message result; disambiguate by its transcript text. */
    message: "search.message",
    /** One channel result; disambiguate by its title. */
    channel: "search.channel",
    /** Loads the next page of channel results; only rendered while more exist. */
    channelsMore: "search.channels-more",
    /** The notice standing in for a search the environment has no index for; disambiguate by its section. */
    unavailable: "search.unavailable",
  },
  templates: {
    /** Header action starting a new template on the management page. */
    create: "templates.create",
    /** One template row on the management page; disambiguate by name. */
    row: "templates.row",
    /** Delete action on a template row. */
    rowDelete: "templates.row.delete",
    /** Confirmation that deleting the template loses its content. */
    confirmDelete: "templates.confirm-delete",
    /** The template editor's title field (the input is inside). */
    name: "templates.name",
    /** The template editor's body field (the textarea is inside). */
    body: "templates.body",
    /** Saves the template being created or edited. */
    save: "templates.save",
  },
  scheduled: {
    /** Header action starting a new scheduled message. */
    create: "scheduled.create",
    /** One scheduled-message card on the page; disambiguate by its internal name. */
    card: "scheduled.card",
    /** Edit action on a scheduled-message card. */
    cardEdit: "scheduled.card.edit",
    /** Delete action on a scheduled-message card. */
    cardDelete: "scheduled.card.delete",
    /** A composer tab (Add File, Text, Date & Time, Recipients); disambiguate by label. */
    tab: "scheduled.tab",
    /** The internal-name field; one per content tab, so select the visible input inside. */
    name: "scheduled.name",
    /** The text-to-generate-audio box (the textarea is inside). */
    text: "scheduled.text",
    /** The recurrence choices ("Custom Recurrence" / "Does Not Repeat"); disambiguate by label. */
    recurrence: "scheduled.recurrence",
    /** Publishes a new schedule, or saves edits to an existing one. */
    publish: "scheduled.publish",
  },
} as const;

type Leaves<T> = T extends string ? T : { [K in keyof T]: Leaves<T[K]> }[keyof T];

/**
 * Union of every registered test id value.
 */
export type TestId = Leaves<typeof TestIds>;
